import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import axios from "axios";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { clearContactSearchCache } from "./contactSearchCache";
import {
  CONTACT_UNIQUENESS_DEBOUNCE_MS,
  CONTACT_UNIQUENESS_MESSAGES,
  EMPTY_CONTACT_INLINE_ERRORS,
  findContactByEmail,
  findContactByPhone,
  isDuplicateContactMatch,
  isValidEmailForUniquenessCheck,
  isValidPhoneForUniquenessCheck,
  type ContactInlineValidationErrors,
  type ContactSearchScope,
} from "./contactUniqueness";

function isRequestCancelled(err: unknown): boolean {
  return axios.isCancel(err) || (err as { code?: string })?.code === "ERR_CANCELED";
}

type UniquenessField = keyof ContactInlineValidationErrors;

interface FieldCheckConfig {
  field: UniquenessField;
  /** Current input — used to clear errors as soon as the field is emptied. */
  immediateValue: string;
  debouncedValue: string;
  enabled: boolean;
  shouldCheck: (value: string) => boolean;
  runCheck: (value: string, signal: AbortSignal) => Promise<boolean>;
}

function useFieldUniquenessCheck(
  open: boolean,
  config: FieldCheckConfig,
  setErrors: Dispatch<SetStateAction<ContactInlineValidationErrors>>,
) {
  const abortRef = useRef<AbortController | null>(null);
  const lastCheckedRef = useRef<{ value: string; isDuplicate: boolean } | null>(null);
  const { field, immediateValue, debouncedValue, enabled, shouldCheck, runCheck } = config;

  useEffect(() => {
    if (!open || !enabled) {
      abortRef.current?.abort();
      abortRef.current = null;
      lastCheckedRef.current = null;
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const immediate = immediateValue.trim();
    if (!shouldCheck(immediate)) {
      abortRef.current?.abort();
      abortRef.current = null;
      lastCheckedRef.current = null;
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const value = debouncedValue.trim();
    if (!shouldCheck(value)) {
      abortRef.current?.abort();
      abortRef.current = null;
      lastCheckedRef.current = null;
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    const cached = lastCheckedRef.current;
    if (cached?.value === value) {
      setErrors((prev) => ({
        ...prev,
        [field]: cached.isDuplicate ? getDuplicateMessage(field) : "",
      }));
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    void (async () => {
      try {
        const isDuplicate = await runCheck(value, controller.signal);
        if (controller.signal.aborted) return;
        lastCheckedRef.current = { value, isDuplicate };
        setErrors((prev) => ({
          ...prev,
          [field]: isDuplicate ? getDuplicateMessage(field) : "",
        }));
      } catch (err) {
        if (isRequestCancelled(err) || controller.signal.aborted) return;
        lastCheckedRef.current = { value, isDuplicate: false };
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    })();

    return () => {
      controller.abort();
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    };
  }, [open, enabled, immediateValue, debouncedValue, field, shouldCheck, runCheck]);
}

function getDuplicateMessage(field: UniquenessField): string {
  return field === "email" || field === "email2"
    ? CONTACT_UNIQUENESS_MESSAGES.emailDuplicate
    : CONTACT_UNIQUENESS_MESSAGES.phoneDuplicate;
}

export interface UseContactFieldUniquenessInput {
  open: boolean;
  excludeContactId?: string | null;
  firstEmail: string;
  firstPhone: string;
  secondEmail: string;
  secondPhone: string;
  secondPersonEnabled: boolean;
  scope?: ContactSearchScope;
}

export function useContactFieldUniqueness({
  open,
  excludeContactId,
  firstEmail,
  firstPhone,
  secondEmail,
  secondPhone,
  secondPersonEnabled,
  scope,
}: UseContactFieldUniquenessInput): ContactInlineValidationErrors {
  const [errors, setErrors] = useState<ContactInlineValidationErrors>(EMPTY_CONTACT_INLINE_ERRORS);

  const debouncedFirstEmail = useDebouncedValue(firstEmail, CONTACT_UNIQUENESS_DEBOUNCE_MS);
  const debouncedFirstPhone = useDebouncedValue(firstPhone, CONTACT_UNIQUENESS_DEBOUNCE_MS);
  const debouncedSecondEmail = useDebouncedValue(secondEmail, CONTACT_UNIQUENESS_DEBOUNCE_MS);
  const debouncedSecondPhone = useDebouncedValue(secondPhone, CONTACT_UNIQUENESS_DEBOUNCE_MS);

  useEffect(() => {
    if (!open) {
      clearContactSearchCache();
      setErrors(EMPTY_CONTACT_INLINE_ERRORS);
    }
  }, [open]);

  const checkEmailDuplicate = useCallback(
    async (value: string, signal: AbortSignal) => {
      const match = await findContactByEmail(value, { signal, scope });
      return isDuplicateContactMatch(match, excludeContactId);
    },
    [excludeContactId, scope],
  );

  const checkPhoneDuplicate = useCallback(
    async (value: string, signal: AbortSignal) => {
      const match = await findContactByPhone(value, { signal, scope });
      return isDuplicateContactMatch(match, excludeContactId);
    },
    [excludeContactId, scope],
  );

  useFieldUniquenessCheck(
    open,
    {
      field: "email",
      immediateValue: firstEmail,
      debouncedValue: debouncedFirstEmail,
      enabled: true,
      shouldCheck: isValidEmailForUniquenessCheck,
      runCheck: checkEmailDuplicate,
    },
    setErrors,
  );

  useFieldUniquenessCheck(
    open,
    {
      field: "mobilePhone",
      immediateValue: firstPhone,
      debouncedValue: debouncedFirstPhone,
      enabled: true,
      shouldCheck: isValidPhoneForUniquenessCheck,
      runCheck: checkPhoneDuplicate,
    },
    setErrors,
  );

  useFieldUniquenessCheck(
    open,
    {
      field: "email2",
      immediateValue: secondEmail,
      debouncedValue: debouncedSecondEmail,
      enabled: secondPersonEnabled,
      shouldCheck: isValidEmailForUniquenessCheck,
      runCheck: checkEmailDuplicate,
    },
    setErrors,
  );

  useFieldUniquenessCheck(
    open,
    {
      field: "mobilePhone2",
      immediateValue: secondPhone,
      debouncedValue: debouncedSecondPhone,
      enabled: secondPersonEnabled,
      shouldCheck: isValidPhoneForUniquenessCheck,
      runCheck: checkPhoneDuplicate,
    },
    setErrors,
  );

  return errors;
}
